import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { useFilialSelecionada } from '@/hooks/useFilialSelecionada';
import apiService from '@/services/api';
import { extractEmployeeNamesFromPdf, extractEmployeeNamesByPage } from '@/utils/pdfText';
import { splitPdfToBlobs, downloadBlob, sanitizeFilename, saveBlobToDirectory, ensureDirectoryWritePermission } from '@/utils/pdfSplit';
import { buildZip } from '@/utils/zip';

const Container = styled.div`
  padding: 24px;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
`;

const Title = styled.h2`
  margin: 0 0 12px 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  color: #6b7280;
`;

const Input = styled.input`
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
`;

const Select = styled.select`
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const Button = styled.button`
  padding: 10px 12px;
  border-radius: 6px;
  border: none;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

type Vendedor = { Controle: number; Nome: string };

const HoleriteAssinatura: React.FC = () => {
  const { filialSelecionada } = useFilialSelecionada();
  const filialId = filialSelecionada?.id || '';

  const [vendedores, setVendedores] = useState<Vendedor[]>([]);
  const [vendedorControle, setVendedorControle] = useState<number | ''>('');
  const [inicio, setInicio] = useState<string>('');
  const [fim, setFim] = useState<string>('');
  const [signerName, setSignerName] = useState<string>('');
  const [signerEmail, setSignerEmail] = useState<string>('');
  const [signerPhone, setSignerPhone] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [lendoPdfs, setLendoPdfs] = useState<boolean>(false);
  const [enviandoAssinaturas, setEnviandoAssinaturas] = useState<boolean>(false);
  const [statusEnvioAssinaturas, setStatusEnvioAssinaturas] = useState<string>('');
  const [modoEnvioAssinatura, setModoEnvioAssinatura] = useState<'test' | 'production'>('test');
  const [pdfResultados, setPdfResultados] = useState<{ nomeArquivo: string; nomesEncontrados: string[]; resolvidos?: Array<{ nomePdf: string; filialNome: string; controle: number | null }> }[]>([]);
  const folderInputRef = useRef<HTMLInputElement | null>(null);
  const splitInputRef = useRef<HTMLInputElement | null>(null);
  const assinaturaInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    (async () => {
      if (!filialId) return;
      try {
        const list = await apiService.listarVendedores(filialId);
        setVendedores(list);
      } catch (e: any) {
        toast.error(e?.response?.data?.error || 'Falha ao carregar vendedores');
      }
    })();
  }, [filialId]);

  const vendedorOptions = useMemo(() => {
    return vendedores.map(v => ({ value: v.Controle, label: v.Nome }));
  }, [vendedores]);

  const handleFolderChange: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    try {
      const files = Array.from(e.target.files || [])
        .filter(f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
      if (files.length === 0) {
        toast.error('Nenhum PDF encontrado na pasta selecionada');
        return;
      }
      setLendoPdfs(true);
      const resultados: { nomeArquivo: string; nomesEncontrados: string[] }[] = [];

      for (const file of files) {
        try {
          const buf = await file.arrayBuffer();
          const nomes = await extractEmployeeNamesFromPdf(buf);
          resultados.push({ nomeArquivo: file.name, nomesEncontrados: nomes });
        } catch {
          resultados.push({ nomeArquivo: file.name, nomesEncontrados: [] });
        }
      }

      // Resolver nomes no backend (filial + controle), em lote para todos os arquivos
      const todosNomes = Array.from(new Set(resultados.flatMap(r => r.nomesEncontrados)));
      let resolvidosMapa = new Map<string, { nomePdf: string; filialNome: string; controle: number | null }>();
      if (todosNomes.length > 0) {
        try {
          const resolved = await apiService.resolverFuncionariosPorNomes(todosNomes);
          for (const r of resolved) {
            const filialNome = r.filialNome || 'não encontrado';
            const controle = r.controle ?? null;
            resolvidosMapa.set(r.nomePdf, { nomePdf: r.nomePdf, filialNome, controle });
          }
        } catch {}
      }

      const resultadosComFilial = resultados.map(r => ({
        ...r,
        resolvidos: r.nomesEncontrados.map(nome => {
          const match = resolvidosMapa.get(nome);
          return { nomePdf: nome, filialNome: match?.filialNome || 'não encontrado', controle: match?.controle ?? null };
        })
      }));

      setPdfResultados(resultadosComFilial);
      if (resultados.every(r => r.nomesEncontrados.length === 0)) {
        toast('Nenhum nome encontrado nos PDFs selecionados');
      } else {
        toast.success('Leitura de PDFs concluída');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Falha ao ler PDFs');
    } finally {
      setLendoPdfs(false);
      if (folderInputRef.current) {
        folderInputRef.current.value = '';
      }
    }
  };

  const handleSplitFolderChange: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    try {
      const files = Array.from(e.target.files || [])
        .filter(f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
      if (files.length === 0) {
        toast.error('Nenhum PDF encontrado na pasta selecionada');
        return;
      }
      setLendoPdfs(true);

      // Tenta obter diretório de saída (File System Access API)
      let outDirHandle: any = null;
      try {
        // @ts-ignore - API específica de Chromium
        if (window && (window as any).showDirectoryPicker) {
          // @ts-ignore
          outDirHandle = await (window as any).showDirectoryPicker();
          const ok = await ensureDirectoryWritePermission(outDirHandle);
          if (!ok) {
            outDirHandle = null;
          }
        }
      } catch (pickErr: any) {
        console.warn('Usuário cancelou a escolha da pasta de saída ou a API não está disponível.', pickErr?.message || pickErr);
      }

      let totalGerados = 0;
      const zipEntries: Array<{ filename: string; blob: Blob }> = [];
      const usedNames = new Set<string>();
      const ensureUnique = (name: string) => {
        let n = name;
        let c = 1;
        while (usedNames.has(n)) {
          const extIdx = name.toLowerCase().lastIndexOf('.pdf');
          if (extIdx > 0) {
            n = `${name.slice(0, extIdx)} (${c}).pdf`;
          } else {
            n = `${name} (${c})`;
          }
          c += 1;
        }
        usedNames.add(n);
        return n;
      };

      for (const file of files) {
        try {
          const buf = await file.arrayBuffer();
          const [blobs, nomesByPage] = await Promise.all([
            splitPdfToBlobs(buf),
            extractEmployeeNamesByPage(buf)
          ]);
          const baseName = file.name.replace(/\.pdf$/i, '');
          for (let i = 0; i < blobs.length; i += 1) {
            const nome = (nomesByPage && nomesByPage[i]) ? ` - ${sanitizeFilename(nomesByPage[i] as string)}` : ` - p${i + 1}`;
            const filename = ensureUnique(`${sanitizeFilename(baseName)}${nome}.pdf`);
            if (outDirHandle) {
              await saveBlobToDirectory(outDirHandle, filename, blobs[i]);
            } else {
              // Fallback: acumula no ZIP para um único download
              zipEntries.push({ filename, blob: blobs[i] });
            }
            totalGerados += 1;
          }
        } catch (err: any) {
          console.warn('Falha ao dividir PDF:', err?.message || err);
        }
      }
      if (outDirHandle) {
        toast.success(`Divisão concluída: ${totalGerados} arquivo(s) gravado(s) na pasta escolhida`);
      } else {
        // Gera um único ZIP com todos os arquivos
        if (zipEntries.length > 0) {
          const zipBlob = await buildZip(zipEntries);
          const ts = new Date().toISOString().replace(/[-:T.Z]/g, '').slice(0, 14);
          downloadBlob(zipBlob, `pdfs-divididos-${ts}.zip`);
        }
        toast.success(`Divisão concluída: ${totalGerados} página(s) preparada(s) para download`);
      }
    } catch (err: any) {
      toast.error(err?.message || 'Falha ao dividir PDFs');
    } finally {
      setLendoPdfs(false);
      if (splitInputRef.current) splitInputRef.current.value = '';
    }
  };

  const handleGerarEnviar = async () => {
    try {
      if (!filialId) {
        toast.error('Selecione uma filial');
        return;
      }
      if (!vendedorControle) {
        toast.error('Selecione um vendedor');
        return;
      }
      if (!inicio || !fim) {
        toast.error('Informe o período (início e fim)');
        return;
      }
      if (!signerName) {
        toast.error('Informe o nome do signatário');
        return;
      }
      setLoading(true);
      const { documentId } = await apiService.criarAssinaturaHoleriteDb({
        filialId,
        vendedorControle: Number(vendedorControle),
        inicio,
        fim,
        signerName,
        signerEmail: signerEmail || '',
        signerPhone: signerPhone || undefined,
      });
      toast.success(`Assinatura criada! Documento: ${documentId}`);
      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      toast.error(e?.response?.data?.error || 'Falha ao criar assinatura');
    }
  };

  const normalizeNome = (s: string): string => (
    (s || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toUpperCase()
  );

  const blobToBase64 = async (blob: Blob): Promise<string> => {
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = String(reader.result || '');
        const idx = result.indexOf(',');
        if (idx < 0) return reject(new Error('Falha ao converter PDF para base64'));
        resolve(result.slice(idx + 1));
      };
      reader.onerror = () => reject(new Error('Falha ao ler arquivo PDF'));
      reader.readAsDataURL(blob);
    });
  };

  const waitRandom = async (minMs: number, maxMs: number) => {
    const ms = Math.floor(Math.random() * (maxMs - minMs + 1)) + minMs;
    await new Promise((resolve) => setTimeout(resolve, ms));
  };

  const handleEnviarAssinaturasFolderChange: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    try {
      const files = Array.from(e.target.files || [])
        .filter(f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
      if (files.length === 0) {
        toast.error('Nenhum PDF encontrado na pasta selecionada');
        return;
      }

      setEnviandoAssinaturas(true);
      setStatusEnvioAssinaturas('Lendo e dividindo PDFs...');

      const paginasFuncionario: Array<{ nomeArquivo: string; nomeFuncionario: string; blob: Blob }> = [];
      let ignoradasDuplicadas = 0;
      let semNome = 0;

      for (const file of files) {
        const buf = await file.arrayBuffer();
        const [blobs, nomesByPage] = await Promise.all([
          splitPdfToBlobs(buf),
          extractEmployeeNamesByPage(buf)
        ]);

        let nomeAnteriorNorm = '';
        for (let i = 0; i < blobs.length; i += 1) {
          const nomeFuncionario = String(nomesByPage?.[i] || '').replace(/\s+/g, ' ').trim();
          const nomeNorm = normalizeNome(nomeFuncionario);
          if (!nomeNorm) {
            semNome += 1;
            continue;
          }
          if (nomeAnteriorNorm && nomeNorm === nomeAnteriorNorm) {
            ignoradasDuplicadas += 1;
            continue;
          }
          nomeAnteriorNorm = nomeNorm;
          paginasFuncionario.push({ nomeArquivo: file.name, nomeFuncionario, blob: blobs[i] });
        }
      }

      if (paginasFuncionario.length === 0) {
        toast.error('Nenhuma página válida de funcionário foi encontrada');
        return;
      }

      setStatusEnvioAssinaturas('Resolvendo funcionários e telefones...');
      const nomesUnicos = Array.from(new Set(paginasFuncionario.map(p => p.nomeFuncionario)));
      let resolvidos: Array<{ nomePdf: string; filialId: string | null; filialNome: string | null; controle: number | null; nomeDb: string | null; celular: string | null }> = [];
      try {
        resolvidos = await apiService.resolverFuncionariosPorNomes(nomesUnicos);
      } catch {}
      const resolvidosMap = new Map<string, { nomePdf: string; filialId: string | null; filialNome: string | null; controle: number | null; nomeDb: string | null; celular: string | null }>();
      for (const r of resolvidos) {
        resolvidosMap.set(normalizeNome(r.nomePdf), r);
      }

      let enviadosOk = 0;
      let enviadosErro = 0;

      for (let i = 0; i < paginasFuncionario.length; i += 1) {
        const item = paginasFuncionario[i];
        const resolved = resolvidosMap.get(normalizeNome(item.nomeFuncionario));
        const signerName = resolved?.nomeDb || item.nomeFuncionario;
        setStatusEnvioAssinaturas(`Enviando ${i + 1}/${paginasFuncionario.length}: ${signerName}`);

        try {
          const pdfBase64 = await blobToBase64(item.blob);
          await apiService.criarAssinaturaFuncionarioPdf({
            mode: modoEnvioAssinatura,
            signerName,
            nomeArquivo: item.nomeArquivo,
            nomeFuncionarioPdf: item.nomeFuncionario,
            filialNome: resolved?.filialNome || null,
            celularFuncionario: resolved?.celular || null,
            pdfBase64,
            fixedTestPhone: '+351968483466',
          });
          enviadosOk += 1;
        } catch (err: any) {
          enviadosErro += 1;
          toast.error(err?.response?.data?.error || `Falha ao processar ${signerName}`);
        }

        if (i < paginasFuncionario.length - 1) {
          setStatusEnvioAssinaturas(`Aguardando 6-10s para próximo envio (${i + 1}/${paginasFuncionario.length})...`);
          await waitRandom(6000, 10000);
        }
      }

      setStatusEnvioAssinaturas('');
      toast.success(`Processo concluído: ${enviadosOk} enviado(s), ${enviadosErro} com falha, ${ignoradasDuplicadas} duplicado(s) ignorado(s), ${semNome} página(s) sem nome.`);
    } catch (err: any) {
      setStatusEnvioAssinaturas('');
      toast.error(err?.message || 'Falha ao processar pasta de PDFs para assinatura');
    } finally {
      setEnviandoAssinaturas(false);
      if (assinaturaInputRef.current) assinaturaInputRef.current.value = '';
    }
  };

  return (
    <Container>
      <Card>
        <Title>Gerar Holerite e Enviar para Assinatura</Title>
        <Grid>
          <Field>
            <Label>Vendedor</Label>
            <Select
              value={String(vendedorControle)}
              onChange={(e) => setVendedorControle(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">Selecione</option>
              {vendedorOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
          </Field>
          <Field>
            <Label>Data de Início</Label>
            <Input type="date" value={inicio} onChange={e => setInicio(e.target.value)} />
          </Field>
          <Field>
            <Label>Data de Fim</Label>
            <Input type="date" value={fim} onChange={e => setFim(e.target.value)} />
          </Field>
          <Field>
            <Label>Nome do Signatário</Label>
            <Input placeholder="Nome completo" value={signerName} onChange={e => setSignerName(e.target.value)} />
          </Field>
          <Field>
            <Label>E-mail do Signatário</Label>
            <Input type="email" placeholder="email@exemplo.com" value={signerEmail} onChange={e => setSignerEmail(e.target.value)} />
          </Field>
          <Field>
            <Label>Telefone do Signatário (opcional, E.164)</Label>
            <Input placeholder="+5511999999999" value={signerPhone} onChange={e => setSignerPhone(e.target.value)} />
          </Field>
        </Grid>
        <Actions>
          <Field style={{ minWidth: 260 }}>
            <Label>Modo de Envio da Assinatura</Label>
            <Select
              value={modoEnvioAssinatura}
              onChange={(e) => setModoEnvioAssinatura(e.target.value as 'test' | 'production')}
              disabled={enviandoAssinaturas || lendoPdfs || loading}
            >
              <option value="test">Teste (envia para +351968483466)</option>
              <option value="production">Produção (envia para celular do funcionário)</option>
            </Select>
          </Field>
          <Button disabled={loading} onClick={handleGerarEnviar}>
            {loading ? 'Processando…' : 'Gerar PDF e Enviar para Assinatura'}
          </Button>
          <input
            ref={folderInputRef}
            type="file"
            multiple
            accept="application/pdf"
            style={{ display: 'none' }}
            // @ts-ignore - atributo específico de Chromium para seleção de pasta
            webkitdirectory=""
            onChange={handleFolderChange}
          />
          <Button
            disabled={lendoPdfs || loading}
            onClick={() => folderInputRef.current?.click()}
            title="Selecionar uma pasta com vários PDFs para leitura local"
          >
            {lendoPdfs ? 'Lendo PDFs…' : 'Ler PDFs de uma pasta'}
          </Button>
          <input
            ref={splitInputRef}
            type="file"
            multiple
            accept="application/pdf"
            style={{ display: 'none' }}
            // @ts-ignore - atributo específico de Chromium para seleção de pasta
            webkitdirectory=""
            onChange={handleSplitFolderChange}
          />
          <Button
            disabled={lendoPdfs || loading}
            onClick={() => splitInputRef.current?.click()}
            title="Dividir cada PDF em arquivos de 1 página (nomeando por funcionário quando possível)"
          >
            {lendoPdfs ? 'Dividindo…' : 'Dividir PDFs (1 página por arquivo)'}
          </Button>
          <input
            ref={assinaturaInputRef}
            type="file"
            multiple
            accept="application/pdf"
            style={{ display: 'none' }}
            // @ts-ignore - atributo específico de Chromium para seleção de pasta
            webkitdirectory=""
            onChange={handleEnviarAssinaturasFolderChange}
          />
          <Button
            disabled={enviandoAssinaturas || lendoPdfs || loading}
            onClick={() => assinaturaInputRef.current?.click()}
            title="Selecionar pasta de PDFs para dividir, enviar à Autentique e mandar link via TextMeBot"
          >
            {enviandoAssinaturas ? 'Enviando para Assinatura…' : `Enviar PDFs para Assinatura (${modoEnvioAssinatura === 'test' ? 'Teste' : 'Produção'})`}
          </Button>
          <Button disabled={loading} onClick={async () => {
            try {
              await apiService.enviarWhatsTesteId1();
              toast.success('WhatsApp (ID 1) enviado');
            } catch (e: any) {
              toast.error(e?.response?.data?.error || 'Falha ao enviar WhatsApp');
            }
          }}>
            Testar WhatsApp (ID 1)
          </Button>
          <Button disabled={loading} onClick={async () => {
            try {
              await apiService.enviarTextMeBotTeste();
              toast.success('TextMeBot enviado');
            } catch (e: any) {
              toast.error(e?.response?.data?.error || 'Falha ao enviar TextMeBot');
            }
          }}>
            Testar TextMeBot
          </Button>
          <Button disabled={loading} onClick={async () => {
            try {
              const result = await apiService.enviarWhatsappProviderTeste();
              toast.success(`WhatsApp (${result.provider || 'provider'}) enviado`);
            } catch (e: any) {
              toast.error(e?.response?.data?.error || 'Falha ao enviar WhatsApp pelo provider ativo');
            }
          }}>
            Testar Provider Ativo
          </Button>
        </Actions>
        {enviandoAssinaturas && (
          <div style={{ marginTop: 10, color: '#4b5563', fontSize: 13 }}>
            {statusEnvioAssinaturas || 'Processando envio...'}
          </div>
        )}
        {!enviandoAssinaturas && (
          <div style={{ marginTop: 10, color: '#6b7280', fontSize: 12 }}>
            {modoEnvioAssinatura === 'test'
              ? 'Modo Teste ativo: os links serão enviados somente para +351968483466.'
              : 'Modo Produção ativo: os links serão enviados para o celular do funcionário (tfunc.Celular -> +55XXXXXXXXXXX).'}
          </div>
        )}
      </Card>

      {pdfResultados && pdfResultados.length > 0 && (
        <Card style={{ marginTop: 16 }}>
          <Title>Resultados da Leitura de PDFs</Title>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
            {pdfResultados.map((r, idx) => (
              <div key={idx} style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 10 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>{r.nomeArquivo}</div>
                {r.nomesEncontrados.length === 0 ? (
                  <div style={{ color: '#6b7280' }}>Nenhum nome encontrado</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: '6px 4px' }}>Filial</th>
                        <th style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: '6px 4px' }}>Controle</th>
                        <th style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: '6px 4px' }}>Nome</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(r.resolvidos || r.nomesEncontrados.map(n => ({ nomePdf: n, filialNome: 'não encontrado', controle: null }))).map((row, i2) => (
                        <tr key={i2}>
                          <td style={{ padding: '6px 4px', borderBottom: '1px solid #f3f4f6' }}>{row.filialNome}</td>
                          <td style={{ padding: '6px 4px', borderBottom: '1px solid #f3f4f6' }}>{row.controle ?? ''}</td>
                          <td style={{ padding: '6px 4px', borderBottom: '1px solid #f3f4f6' }}>{row.nomePdf}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </Container>
  );
};

export default HoleriteAssinatura;


